import React, { useCallback, useRef } from 'react';
import { useCartAdd } from '@wearejh/m2-pwa-cart-gql/lib/hooks/useCardAdd';
import { Obj } from '@wearejh/rx-form/lib/types';
import { Form } from '@wearejh/rx-form/lib/RxForm';
import { DataStatus } from '@wearejh/m2-pwa-engine/lib/types';

import { MessagesModal } from 'src/components/Modal/MessagesModal';
import { AlgoliaContextProvider } from 'src/components/Algolia/SearchDropdown/AlgoliaContext';
import classes from 'src/components/ProductDetail/ProductDetail.scss';

import { ProductActions } from './ProductActions';
import { Product } from './utils/product-detail.types';
import { ProductAttrs } from './utils/product-attrs.types';

export type ProductActionsConnectedProps = {
    attrs: ProductAttrs;
    configurableOptions?: React.ReactNode;
    deliveryOptions: React.ReactNode;
    isEnabled: boolean;
    parentSku?: string;
    product: Product;
    productPrice: React.ReactNode;
    promotions?: React.ReactNode;
    isComingSoon?: boolean;
    isComingSoonText?: string;
};

export function ProductActionsConnected(props: ProductActionsConnectedProps) {
    const sku = props.parentSku ? props.parentSku : props.product.sku;
    const { status, messages, add, clearMessages } = useCartAdd();
    const isPending = status === DataStatus.Pending;
    const addToCartQTY = useRef(1);

    const submit = useCallback(
        (values?: Obj) => {
            const selections: string[] | undefined = values?.selected_options
                ? Object.values(values?.selected_options)
                : undefined;

            add({
                items: [
                    {
                        quantity: values?.qty ?? 1,
                        sku: sku,
                        selected_options: selections ?? undefined,
                    },
                ],
            });

            addToCartQTY.current = values?.qty ?? 1;
        },
        [add, sku],
    );

    return (
        <AlgoliaContextProvider>
            <Form onSubmit={submit}>
                {props.configurableOptions && (
                    <div className={classes.configurableOptions} aria-label="Product Options area">
                        {props.configurableOptions}
                    </div>
                )}

                <div className={classes.deliveryOptions} aria-label="Product delivery options area">
                    {props.deliveryOptions}
                </div>

                {props.promotions && (
                    <div className={classes.promotions} aria-label="Product promotions area">
                        {props.promotions}
                    </div>
                )}

                <div className={classes.productPrice} aria-label="Product price area">
                    {props.productPrice}
                    <h2 className={classes.productDetailSku}>Product Code: {props.product.sku}</h2>
                </div>

                <div className={classes.productStickyAdd} aria-label="Product Add to basket area">
                    {!props.isComingSoon && (
                        <fieldset disabled={isPending || !props.isEnabled}>
                            <legend className="visually-hidden">Add {props.product.name} to your basket.</legend>
                            <ProductActions />
                        </fieldset>
                    )}

                    {props.isComingSoon && (
                        <div className={['button', classes.comingSoonButton].join(' ')}>{props.isComingSoonText}</div>
                    )}
                </div>

                {messages.length > 0 && !isPending && (
                    <MessagesModal messages={messages} title="Sorry!" close={clearMessages} />
                )}
            </Form>
        </AlgoliaContextProvider>
    );
}

export default ProductActionsConnected;
